// src/pages/Users/Inscription/EditDocumentsModal.jsx
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import Button from "../../../components/Button/Button";
import {
  updateDossierInscription,
  uploadCertificatMedical,
  uploadFormulaireLicenceSigne,
  uploadLicenceDefinitive,
  deleteDossierFile,
} from "../../../services/dossierServices";
import {
  FaTimes,
  FaSave,
  FaTrashAlt,
  FaPlusCircle,
  FaGoogleDrive,
  FaFilePdf,
  FaImage,
  FaLink,
  FaTimesCircle,
  FaFileMedical,
  FaFileSignature,
  FaCheckCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";

/*
  Config par défaut : comportement historique.
  AdherentCard peut surcharger cette config via la prop documentsConfig.
*/
const DEFAULT_DOCUMENTS_CONFIG = {
  medicalRequired: true,
  showLicenceSigned: true,
  showLicenceAssurance: true,
};

/* ---------------- Helpers ---------------- */
const SERVER_BASE = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  ""
).replace(/\/+$/, "");

const toAbsoluteUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;

  const clean = u.startsWith("/") ? u : `/${u}`;
  return `${SERVER_BASE}${clean}`;
};

const isDrive = (u) =>
  !!u &&
  /https?:\/\/(drive\.google\.com|docs\.google\.com|googleusercontent\.com)/i.test(
    u
  );

const isImage = (u) =>
  !!u && /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(u.split("?")[0]);

const isPdf = (u) =>
  !!u && (u.toLowerCase().endsWith(".pdf") || u.toLowerCase().includes("pdf"));

// Prend 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ssZ', Date, etc. et renvoie 'YYYY-MM-DD'
const toInputDateSafe = (v) => {
  if (!v) return "";

  if (typeof v === "string") {
    const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }

  const d = new Date(v);
  if (isNaN(d.getTime())) return "";

  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return `${y}-${m}-${dd}`;
};

/* Icône “état document” */
const BigDocButton = ({ url, onClick }) => {
  const has = !!url;
  let Icon = FaTimesCircle;
  let color = "text-slate-300";
  let label = "Aucun document";

  if (has) {
    if (isDrive(url)) {
      Icon = FaGoogleDrive;
      color = "text-sky-600";
      label = "Lien Google Drive";
    } else if (isPdf(url)) {
      Icon = FaFilePdf;
      color = "text-violet-600";
      label = "Fichier PDF";
    } else if (isImage(url)) {
      Icon = FaImage;
      color = "text-emerald-600";
      label = "Image";
    } else {
      Icon = FaLink;
      color = "text-slate-600";
      label = "Lien";
    }
  }

  return (
    <button
      type="button"
      onClick={has ? onClick : undefined}
      className={`group flex w-full flex-col items-center justify-center rounded-2xl border border-dashed p-4 transition sm:p-5 ${
        has
          ? "border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          : "cursor-not-allowed border-slate-300 bg-slate-50/50 dark:bg-slate-900/30"
      }`}
      title={has ? "Ouvrir le document" : "Aucun document"}
    >
      <Icon
        className={`mb-1.5 text-4xl sm:mb-2 sm:text-5xl ${color} ${
          has ? "group-hover:scale-105" : ""
        }`}
      />

      <div
        className={`text-xs sm:text-sm ${
          has ? "text-slate-800 dark:text-slate-100" : "text-slate-400"
        }`}
      >
        {label}
      </div>

      {has && (
        <div className="mt-0.5 text-[11px] text-slate-500 sm:mt-1 sm:text-xs">
          Cliquer pour ouvrir
        </div>
      )}
    </button>
  );
};

/* Cadre section générique */
const SectionCard = ({ title, icon, children }) => (
  <div className="rounded-2xl bg-white/90 p-4 ring-1 ring-slate-200 dark:bg-slate-900/70 dark:ring-slate-800">
    <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
      <span className="text-base sm:text-lg">{icon}</span>
      <h4 className="text-sm font-semibold sm:text-base">{title}</h4>
    </div>

    <div className="mt-3">{children}</div>
  </div>
);

/* ---------------- Main Modal ---------------- */
const EditDocumentsModal = ({
  isOpen,
  onClose,
  dossier,
  refreshDossiers,
  documentsConfig = DEFAULT_DOCUMENTS_CONFIG,
}) => {
  const docConfig = {
    ...DEFAULT_DOCUMENTS_CONFIG,
    ...documentsConfig,
  };

  const [saving, setSaving] = useState(false);

  // États pour afficher/masquer les formulaires
  const [showFormCM, setShowFormCM] = useState(false);
  const [showFormSig, setShowFormSig] = useState(false);
  const [showFormLic, setShowFormLic] = useState(false);

  // Modes & inputs pour chaque section
  const [modeCM, setModeCM] = useState("upload");
  const [fileCM, setFileCM] = useState(null);
  const [urlCM, setUrlCM] = useState("");
  const [dateCM, setDateCM] = useState("");

  const [modeSig, setModeSig] = useState("upload");
  const [fileSig, setFileSig] = useState(null);
  const [urlSig, setUrlSig] = useState("");

  const [modeLic, setModeLic] = useState("upload");
  const [fileLic, setFileLic] = useState(null);
  const [urlLic, setUrlLic] = useState("");

  const dateInputRef = useRef(null);

  // Valeurs actuelles
  const currentCM = dossier?.cm_url || "";
  const currentSignee =
    dossier?.formulaire_licence_signe ||
    dossier?.formulaire_licence_signee ||
    "";
  const currentLicence =
    dossier?.licence_url ||
    dossier?.licence_assurance_url ||
    dossier?.licence_assurance ||
    "";

  // Réinitialisation à l’ouverture
  useEffect(() => {
    if (!isOpen || !dossier?.id) return;

    setShowFormCM(false);
    setShowFormSig(false);
    setShowFormLic(false);

    setModeCM(
      currentCM && currentCM.startsWith("/uploads/")
        ? "upload"
        : currentCM
        ? "link"
        : "upload"
    );
    setFileCM(null);
    setUrlCM(currentCM || "");
    setDateCM(toInputDateSafe(dossier?.date_cm));

    setModeSig(
      currentSignee && currentSignee.startsWith("/uploads/")
        ? "upload"
        : currentSignee
        ? "link"
        : "upload"
    );
    setFileSig(null);
    setUrlSig(currentSignee || "");

    setModeLic(
      currentLicence && currentLicence.startsWith("/uploads/")
        ? "upload"
        : currentLicence
        ? "link"
        : "upload"
    );
    setFileLic(null);
    setUrlLic(currentLicence || "");

    setSaving(false);
  }, [
    isOpen,
    dossier?.id,
    dossier?.date_cm,
    currentCM,
    currentSignee,
    currentLicence,
  ]);

  if (!isOpen || !dossier?.id) return null;

  /* ---------- Actions génériques ---------- */
  const openUrl = (u) => {
    if (!u) return;
    window.open(toAbsoluteUrl(u), "_blank", "noopener,noreferrer");
  };

  const removeField = async (field, label) => {
    if (!window.confirm(`Supprimer « ${label} » ?`)) return;

    try {
      setSaving(true);
      await deleteDossierFile(dossier.id, field);
      toast.success(`${label} supprimé.`);
      await refreshDossiers?.();
    } catch (e) {
      console.error(e);
      toast.error(`Échec de la suppression de « ${label} ».`);
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Sauvegardes par section ---------- */

  // 1) Médical — document fichier OU lien
  const saveCM = async () => {
    if (modeCM === "upload" && !fileCM) {
      toast.error("Choisissez un fichier PDF ou image.");
      return;
    }

    if (modeCM === "link" && urlCM && !/^https?:\/\//i.test(urlCM)) {
      toast.error("URL invalide.");
      return;
    }

    setSaving(true);

    try {
      if (modeCM === "upload") {
        await uploadCertificatMedical(
          dossier.id,
          fileCM,
          dateCM ? { date_cm: dateCM } : undefined
        );
      } else {
        if (currentCM?.startsWith?.("/uploads/") && urlCM) {
          try {
            await deleteDossierFile(dossier.id, "cm_url");
          } catch {}
        }

        const payload = { cm_url: urlCM || null };
        if (dateCM) payload.date_cm = dateCM;

        await updateDossierInscription(dossier.id, payload);
      }

      await refreshDossiers?.();
      setShowFormCM(false);
      toast.success("Document médical mis à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement du document médical.");
    } finally {
      setSaving(false);
    }
  };

  // 1-bis) Médical — sauvegarde indépendante de la date
  const saveCMDate = async () => {
    if (!dateCM) {
      toast.error("Indiquez la date du certificat/attestation.");
      if (dateInputRef.current) dateInputRef.current.focus();
      return;
    }

    setSaving(true);

    try {
      await updateDossierInscription(dossier.id, { date_cm: dateCM });
      await refreshDossiers?.();
      toast.success("Date du document médical enregistrée.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement de la date.");
    } finally {
      setSaving(false);
    }
  };

  // 2) Formulaire signé
  const saveSignee = async () => {
    if (modeSig === "upload" && !fileSig) {
      toast.error("Choisissez un fichier PDF ou image.");
      return;
    }

    if (modeSig === "link" && urlSig && !/^https?:\/\//i.test(urlSig)) {
      toast.error("URL invalide.");
      return;
    }

    setSaving(true);

    try {
      if (modeSig === "upload") {
        await uploadFormulaireLicenceSigne(dossier.id, fileSig);
      } else {
        if (currentSignee?.startsWith?.("/uploads/") && urlSig) {
          try {
            await deleteDossierFile(dossier.id, "formulaire_licence_signe");
          } catch {}
        }

        await updateDossierInscription(dossier.id, {
          formulaire_licence_signe: urlSig || null,
          formulaire_licence_signee: urlSig || null,
        });
      }

      await refreshDossiers?.();
      setShowFormSig(false);
      toast.success("Formulaire signé mis à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  // 3) Licence / assurance
  const saveLicence = async () => {
    if (modeLic === "upload" && !fileLic) {
      toast.error("Choisissez un fichier PDF ou image.");
      return;
    }

    if (modeLic === "link" && urlLic && !/^https?:\/\//i.test(urlLic)) {
      toast.error("URL invalide.");
      return;
    }

    setSaving(true);

    try {
      if (modeLic === "upload") {
        await uploadLicenceDefinitive(dossier.id, fileLic);
      } else {
        if (currentLicence?.startsWith?.("/uploads/") && urlLic) {
          try {
            await deleteDossierFile(dossier.id, "licence_url");
          } catch {}
        }

        await updateDossierInscription(dossier.id, {
          licence_url: urlLic || null,
          licence_assurance_url: urlLic || null,
          licence_assurance: urlLic || null,
        });
      }

      await refreshDossiers?.();
      setShowFormLic(false);
      toast.success("Licence / Assurance mise à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- UI ---------- */

  const Header = (
    <div className="sticky top-0 z-10 flex items-center justify-between bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6">
      <div>
        <h3 className="text-lg font-bold tracking-tight text-white sm:text-xl">
          Documents d’inscription
        </h3>

        <p className="text-[11px] text-white/90 sm:text-xs">
          Cliquez sur l’icône pour ouvrir. Utilisez “Ajouter/Modifier” pour
          déposer un fichier ou un lien. “Supprimer” retire le document.
        </p>
      </div>

      <button
        onClick={() => !saving && onClose?.()}
        className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white/15 text-white transition hover:bg-white/25 sm:h-9 sm:w-9"
        aria-label="Fermer"
        type="button"
        title="Fermer"
      >
        <FaTimes />
      </button>
    </div>
  );

  const FormUploadOrLink = ({
    mode,
    setMode,
    file,
    setFile,
    url,
    setUrl,
    extraBelow,
  }) => (
    <div className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-[13px] sm:text-sm">
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            value="upload"
            checked={mode === "upload"}
            onChange={() => setMode("upload")}
          />
          Téléverser un fichier
        </label>

        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            value="link"
            checked={mode === "link"}
            onChange={() => setMode("link")}
          />
          Coller un lien Drive
        </label>
      </div>

      {mode === "upload" ? (
        <input
          type="file"
          accept="application/pdf,image/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
        />
      ) : (
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://drive.google.com/..."
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
        />
      )}

      {extraBelow}
    </div>
  );

  const btnSmall = "!rounded-lg !px-2.5 !py-1.5 !text-xs";
  const btnDangerSmall = `${btnSmall} !bg-rose-600 hover:!bg-rose-700`;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose?.()}
    >
      <div
        className="relative flex max-h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white/95 shadow-2xl ring-1 ring-slate-200/60 dark:bg-slate-900/95 dark:ring-slate-800/60"
        onClick={(e) => e.stopPropagation()}
      >
        {Header}

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-4 sm:p-6">
          {/* Médical */}
          <SectionCard
            title={
              docConfig.medicalRequired
                ? "Médical (certificat / attestation)"
                : "Médical (certificat / attestation) — optionnel"
            }
            icon={<FaFileMedical />}
          >
            <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_auto]">
              <BigDocButton
                url={currentCM}
                onClick={() => openUrl(currentCM)}
              />

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  text={currentCM ? "Modifier" : "Ajouter"}
                  icon={FaPlusCircle}
                  onClick={() => setShowFormCM((s) => !s)}
                  className={`${btnSmall} gap-1.5`}
                />

                {currentCM && (
                  <Button
                    type="button"
                    text="Supprimer"
                    icon={FaTrashAlt}
                    onClick={() => removeField("cm_url", "Document médical")}
                    className={`${btnDangerSmall} gap-1.5`}
                  />
                )}
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_auto]">
              <div>
                <label className="mb-1 block text-[13px] font-medium">
                  Date figurant sur le document
                </label>

                <input
                  ref={dateInputRef}
                  type="date"
                  value={dateCM}
                  onChange={(e) => setDateCM(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                />

                <p className="mt-1 text-[12px] text-slate-500">
                  {docConfig.medicalRequired
                    ? "Vous pouvez enregistrer la date indépendamment du fichier/lien."
                    : "Ce document est optionnel. Vous pouvez quand même renseigner une date si un document est déposé."}
                </p>
              </div>

              <Button
                type="button"
                text={saving ? "Enregistrement…" : "Enregistrer la date"}
                icon={FaSave}
                onClick={saveCMDate}
                disabled={saving}
                className={`${btnSmall} gap-2`}
              />
            </div>

            {showFormCM && (
              <div className="mt-3 rounded-xl border border-slate-200 p-3 dark:border-slate-800">
                <FormUploadOrLink
                  mode={modeCM}
                  setMode={setModeCM}
                  file={fileCM}
                  setFile={setFileCM}
                  url={urlCM}
                  setUrl={setUrlCM}
                  extraBelow={
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        text={
                          saving ? "Enregistrement…" : "Enregistrer le document"
                        }
                        icon={FaSave}
                        onClick={saveCM}
                        disabled={saving}
                        className={`${btnSmall}`}
                      />

                      <Button
                        type="button"
                        text="Fermer"
                        icon={FaTimes}
                        onClick={() => setShowFormCM(false)}
                        className={`${btnSmall}`}
                      />
                    </div>
                  }
                />
              </div>
            )}
          </SectionCard>

          {/* Formulaire signé */}
          {docConfig.showLicenceSigned && (
            <SectionCard
              title="Formulaire de licence signé"
              icon={<FaFileSignature />}
            >
              <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_auto]">
                <BigDocButton
                  url={currentSignee}
                  onClick={() => openUrl(currentSignee)}
                />

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    text={currentSignee ? "Modifier" : "Ajouter"}
                    icon={FaPlusCircle}
                    onClick={() => setShowFormSig((s) => !s)}
                    className={`${btnSmall} gap-1.5`}
                  />

                  {currentSignee && (
                    <Button
                      type="button"
                      text="Supprimer"
                      icon={FaTrashAlt}
                      onClick={() =>
                        removeField(
                          "formulaire_licence_signe",
                          "Formulaire signé"
                        )
                      }
                      className={`${btnDangerSmall} gap-1.5`}
                    />
                  )}
                </div>
              </div>

              {showFormSig && (
                <div className="mt-3 rounded-xl border border-slate-200 p-3 dark:border-slate-800">
                  <FormUploadOrLink
                    mode={modeSig}
                    setMode={setModeSig}
                    file={fileSig}
                    setFile={setFileSig}
                    url={urlSig}
                    setUrl={setUrlSig}
                    extraBelow={
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          text={saving ? "Enregistrement…" : "Enregistrer"}
                          icon={FaSave}
                          onClick={saveSignee}
                          disabled={saving}
                          className={`${btnSmall}`}
                        />

                        <Button
                          type="button"
                          text="Fermer"
                          icon={FaTimes}
                          onClick={() => setShowFormSig(false)}
                          className={`${btnSmall}`}
                        />
                      </div>
                    }
                  />
                </div>
              )}
            </SectionCard>
          )}

          {/* Licence / Assurance */}
          {docConfig.showLicenceAssurance && (
            <SectionCard
              title="Licence / Assurance (optionnel)"
              icon={<FaCheckCircle />}
            >
              <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[1fr_auto]">
                <BigDocButton
                  url={currentLicence}
                  onClick={() => openUrl(currentLicence)}
                />

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    text={currentLicence ? "Modifier" : "Ajouter"}
                    icon={FaPlusCircle}
                    onClick={() => setShowFormLic((s) => !s)}
                    className={`${btnSmall} gap-1.5`}
                  />

                  {currentLicence && (
                    <Button
                      type="button"
                      text="Supprimer"
                      icon={FaTrashAlt}
                      onClick={() =>
                        removeField("licence_url", "Licence / Assurance")
                      }
                      className={`${btnDangerSmall} gap-1.5`}
                    />
                  )}
                </div>
              </div>

              {showFormLic && (
                <div className="mt-3 rounded-xl border border-slate-200 p-3 dark:border-slate-800">
                  <FormUploadOrLink
                    mode={modeLic}
                    setMode={setModeLic}
                    file={fileLic}
                    setFile={setFileLic}
                    url={urlLic}
                    setUrl={setUrlLic}
                    extraBelow={
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          text={saving ? "Enregistrement…" : "Enregistrer"}
                          icon={FaSave}
                          onClick={saveLicence}
                          disabled={saving}
                          className={`${btnSmall}`}
                        />

                        <Button
                          type="button"
                          text="Fermer"
                          icon={FaTimes}
                          onClick={() => setShowFormLic(false)}
                          className={`${btnSmall}`}
                        />
                      </div>
                    }
                  />
                </div>
              )}
            </SectionCard>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditDocumentsModal;